import instance from 'api/axiosConfig';
import { MainTestSingular } from 'api/maintestApi';
import { LoadingView } from 'components/shared/Loader';
import { NotAlone } from 'components/shared/NotAlone';
import { TestForm } from 'components/shared/TestForm';
import { FC, useEffect, useState } from 'react';
import { selectExaminationID } from 'redux/examination/selectors';
import {
  selectMainTestData,
  selectMainTestError,
  selectMaintestIsReady,
} from 'redux/maintest/selectors';
import { getMainTestQuestions } from 'redux/maintest/thunks';
import { useAppDispatch, useAppSelector } from 'redux/store';
import { answers } from 'utils/answers';
import { maintestSingleAnswerURL, maintestAllAnswerURL } from 'utils/paths';
import { TestManual } from './MainTestManual';
import { MainTestSummary } from './MainTestSummary';
import { TestSwitchCases } from './types';
import { ErrorPageView } from '../Error';
import { requestWithRetry } from 'utils/retryRequest';
import { REQUEST_DELAY_TIME_MS } from 'utils/validation';

const maintestAnswers: MainTestSingular[] = [];

const MainTest: FC = () => {
  const dispatch = useAppDispatch();

  useEffect(() => {
    void dispatch(getMainTestQuestions());
  }, []);

  const examinationId = useAppSelector(selectExaminationID);
  const testQuestionsData = useAppSelector(selectMainTestData);
  const maintestError = useAppSelector(selectMainTestError);

  const maintestIsReady = useAppSelector(selectMaintestIsReady);
  const [isLoading, setIsLoading] = useState(true);
  useEffect(() => {
    if (!maintestIsReady) return;

    setTimeout(() => {
      setIsLoading(false);
    }, REQUEST_DELAY_TIME_MS * 2);
  }, [maintestIsReady]);

  const testLength = testQuestionsData.length;
  const [position, setPosition] = useState(0);

  const [route, setRoute] = useState(TestSwitchCases.instruction);
  const routeTo = (name: TestSwitchCases) => {
    setRoute(name);
  };

  const [wasSensitive, setWasSensitive] = useState(false);

  const questionsRoute = (selectedAnswer: string) => {
    const isSensitive =
      testQuestionsData[position].sensitive &&
      ['a3', 'a4', 'a5'].includes(selectedAnswer);

    const answerData = {
      question: testQuestionsData[position].id,
      answer: selectedAnswer,
    };

    maintestAnswers.push(answerData);
    void instance.post(maintestSingleAnswerURL(examinationId), answerData);
    setPosition((prevValue) => prevValue + 1);
    if (position < testLength - 1) {
      if (isSensitive) {
        setWasSensitive(isSensitive);
        routeTo(TestSwitchCases.notAlone);
      }
    } else {
      void requestWithRetry(maintestAllAnswerURL(examinationId), true, {
        postBody: maintestAnswers,
      });

      if (wasSensitive || isSensitive) {
        routeTo(TestSwitchCases.notAlone);
      } else {
        routeTo(TestSwitchCases.summary);
      }
    }
  };

  const notAloneRoute = () => {
    if (position <= testLength - 1) {
      routeTo(TestSwitchCases.test);
    } else {
      routeTo(TestSwitchCases.summary);
    }
  };

  if (maintestError.error)
    return <ErrorPageView errorCode={maintestError.details} />;

  switch (route) {
    case TestSwitchCases.instruction:
      if (isLoading) return <LoadingView />;
      return <TestManual routeTo={() => routeTo(TestSwitchCases.test)} />;
    case TestSwitchCases.test:
      if (!testQuestionsData[position]) return <LoadingView />;
      return (
        <TestForm
          id={testQuestionsData[position].id}
          position={position + 1}
          testLength={testLength}
          question={testQuestionsData[position].question}
          answers={answers}
          questionsRoute={questionsRoute}
        />
      );
    case TestSwitchCases.summary:
      return <MainTestSummary />;
    case TestSwitchCases.notAlone:
      return <NotAlone routeTo={notAloneRoute} />;

    default:
      return null;
  }
};
export default MainTest;
